import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { ActionService } from "../../../../services/action.service";
import { FileUploadService } from "../../../../services/file-upload.service";
import { DataDetailDialogComponent } from "../../shared/data-detail-dialog/data-detail-dialog.component";
import type { OutputRef } from "../../../../model/block.types";

/**
 * Inline pill chip for a single output file from a background job.
 * Shows a type icon and filename; clicking opens a dropdown menu with
 * Download, View, and Add to map (GeoJSON only) actions.
 */
@Component({
  selector: "app-output-ref-chip",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatMenu, MatMenuItem, MatMenuTrigger],
  templateUrl: "./output-ref-chip.component.html",
  styleUrl: "./output-ref-chip.component.scss",
})
export class OutputRefChipComponent {
  readonly ref = input.required<OutputRef>();

  readonly #fileUpload = inject(FileUploadService);
  readonly #actions = inject(ActionService);
  readonly #dialog = inject(MatDialog);

  readonly loaded = signal(false);
  readonly viewing = signal(false);

  /** Authenticated URL for download/view — adds ?token= or ?api_key= so the browser can fetch without headers. */
  protected readonly authUrl = computed(() => {
    const url = this.ref().url;
    return url ? this.#fileUpload.authenticatedUrl(url) : null;
  });

  protected readonly icon = computed(() => {
    const cat = this.ref().content_category?.toLowerCase();
    const mime = this.ref().content_type?.toLowerCase() ?? "";
    const ext = this.ref().filename.split(".").pop()?.toLowerCase() ?? "";
    if (cat === "geojson" || mime.includes("geo+json") || ext === "geojson") return "map";
    if (cat === "csv" || mime.includes("csv") || ext === "csv") return "table_view";
    if (cat === "pdf" || mime.includes("pdf") || ext === "pdf") return "picture_as_pdf";
    if (cat === "image" || mime.startsWith("image/")) return "image";
    if (cat === "json" || mime.includes("json") || ext === "json") return "data_object";
    return "description";
  });

  protected readonly isGeoJson = computed(() => {
    const cat = this.ref().content_category?.toLowerCase();
    const mime = this.ref().content_type?.toLowerCase() ?? "";
    const ext = this.ref().filename.split(".").pop()?.toLowerCase() ?? "";
    return cat === "geojson" || mime.includes("geo+json") || ext === "geojson";
  });

  protected readonly isViewable = computed(() => !!this.ref().url);

  protected view(): void {
    if (!this.isGeoJson()) {
      window.open(this.authUrl()!, "_blank");
      return;
    }
    const url = this.ref().url;
    if (!url || this.viewing()) return;
    this.viewing.set(true);
    const extras = { filename: this.ref().filename, size_bytes: 0 };
    firstValueFrom(this.#fileUpload.fetchFileContent(url, this.ref().content_type ?? "application/geo+json", extras))
      .then((result) => {
        this.#dialog.open(DataDetailDialogComponent, {
          data: { data: result, title: this.ref().filename },
          width: "90vw",
          maxWidth: "1000px",
          minHeight: "350px",
          maxHeight: "90vh",
        });
      })
      .catch((err) => console.error("OutputRefChipComponent: view failed", err))
      .finally(() => this.viewing.set(false));
  }

  protected async download(): Promise<void> {
    const url = this.authUrl();
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = this.ref().filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("OutputRefChipComponent: download failed", err);
    }
  }

  /**
   * Delegates to the canonical add_layer action (evidence-layer-ui unification): ONE add
   * implementation for every card — ActionService routes plain geojson through the legacy
   * fetch/prepare/addGeoJsonLayer flow exactly as before, and a contract-bearing context
   * (not producible from a legacy OutputRef) would route through EvidenceLayerService.
   */
  protected addToMap(): void {
    const url = this.ref().url;
    if (!url) return;
    this.#actions.execute("add_layer", {
      url,
      filename: this.ref().filename,
    });
  }
}
